import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Download, Loader2, ImageIcon, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import JSZip from "jszip";
import { downloadUrlAsFile } from "@/lib/download";

interface ShareItem {
  id: string;
  fileName: string;
  imageUrl: string;
}

interface ShareData {
  name: string;
  createdAt: string;
  items: ShareItem[];
}

export default function ShareView() {
  const { token } = useParams();
  const [data, setData] = useState<ShareData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (token) fetchShareData();
  }, [token]);

  const fetchShareData = async () => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const res = await fetch(
        `${supabaseUrl}/functions/v1/get-share-project?token=${token}`
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error || "Share project not found");
        setLoading(false);
        return;
      }
      const json = await res.json();
      setData(json);
    } catch {
      setError("Could not load share project");
    }
    setLoading(false);
  };

  const downloadFile = (url: string, filename: string) => {
    void downloadUrlAsFile(url, filename);
  };

  const downloadAll = async () => {
    if (!data || data.items.length === 0) return;
    setDownloading(true);
    try {
      const zip = new JSZip();
      const promises = data.items.map(async (item) => {
        const res = await fetch(item.imageUrl);
        const blob = await res.blob();
        zip.file(item.fileName, blob);
      });
      await Promise.all(promises);
      const content = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(content);
      downloadFile(url, `${data.name}.zip`);
      URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch {
      toast.error("Failed to create zip");
    }
    setDownloading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <ImageIcon className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Not Found</h1>
          <p className="text-muted-foreground">{error || "This share link is invalid or has expired."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">{data.name}</h1>
            <p className="text-sm text-muted-foreground">
              {data.items.length} image{data.items.length !== 1 ? "s" : ""}
            </p>
          </div>
          {data.items.length > 0 && (
            <Button onClick={downloadAll} disabled={downloading}>
              {downloading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Download All
            </Button>
          )}
        </div>
      </header>

      {/* Grid */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {data.items.length === 0 ? (
          <div className="text-center py-20">
            <ImageIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No images in this collection.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {data.items.map((item) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="group relative rounded-lg overflow-hidden bg-secondary border border-border cursor-pointer"
                onClick={() => setLightboxUrl(item.imageUrl)}
              >
                <div className="aspect-square">
                  <img src={item.imageUrl} alt={item.fileName} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="absolute inset-0 bg-background/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadFile(item.imageUrl, item.fileName);
                    }}
                  >
                    <Download className="w-4 h-4 mr-1" /> Download
                  </Button>
                </div>
                <p className="px-2 py-1.5 text-xs text-muted-foreground truncate">{item.fileName}</p>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxUrl && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-background/90 backdrop-blur-sm flex items-center justify-center p-8"
            onClick={() => setLightboxUrl(null)}
          >
            <button
              className="absolute top-6 right-6 text-muted-foreground hover:text-foreground"
              onClick={() => setLightboxUrl(null)}
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={lightboxUrl}
              alt=""
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
